/* Deco Upsell — one-tap add-to-cart for the upsell widgets.
   Reuses the theme's cart plumbing: on regular pages the item is added and
   the cart drawer re-renders (and opens); on the cart page the cart
   sections re-render in place, exactly like a quantity change. */
(function () {
  if (customElements.get('upsell-widget')) return;

  /* Zone-aware free-shipping bar — the Delivery Zones app is the source of
     truth. Zone resolution order:
       1. dz_zone_info (localStorage) — a real address check from the cart
          page or product-page checker; authoritative.
       2. dz_ip_zone (sessionStorage) — the visitor's zone guessed from IP
          via the app's /api/detect-zone (fetched once per session).
       3. Unknown — show only the universal minimum-order stage; above it
          the bar hides rather than promising a wrong threshold.
     IMPORTANT: apply() must not touch the DOM when nothing changed — the
     cart page watches these sections with MutationObservers and re-runs
     its shipping check on any mutation (this caused a calc/reset loop). */
  var DZ_ZONE_KEY = 'dz_zone_info';
  var DZ_ZONE_TTL = 60 * 60 * 1000; // an address check is only trusted for the current shopping session
  // the zone record used to live in localStorage for 7 days — a stale address
  // from an old test kept relabeling the meter; clear any legacy record.
  try { localStorage.removeItem('dz_zone_info'); } catch (e) {}
  var DZ_IP_KEY = 'dz_ip_zone';
  var DZ_IP_TTL = 6 * 3600 * 1000;
  var DZ_IP_NEG_TTL = 30 * 60 * 1000;
  var DZ_DETECT_URL = 'https://delivery-zones-manager.vercel.app/api/detect-zone';

  function dzZoneInfo() {
    try {
      var raw = sessionStorage.getItem(DZ_ZONE_KEY);
      if (!raw) return null;
      var z = JSON.parse(raw);
      if (!z || !z.ts || Date.now() - z.ts > DZ_ZONE_TTL) return null;
      return z;
    } catch (e) {
      return null;
    }
  }

  function dzIpZone() {
    try {
      var raw = sessionStorage.getItem(DZ_IP_KEY);
      if (!raw) return null;
      var z = JSON.parse(raw);
      if (!z || !z.ts) return null;
      var ttl = z.none ? DZ_IP_NEG_TTL : DZ_IP_TTL;
      if (Date.now() - z.ts > ttl) return null;
      return z;
    } catch (e) {
      return null;
    }
  }

  function dzFetchIpZone() {
    if (window.__dzIpZonePromise) return window.__dzIpZonePromise;
    window.__dzIpZonePromise = fetch(DZ_DETECT_URL)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var record;
        if (data && data.found) {
          record = {
            allowed: true,
            zoneName: (data.zoneName || '').trim(),
            deliveryCost: data.deliveryCost || 0,
            freeDeliveryAbove: data.freeDeliveryAbove || 0,
            minOrderAmount: data.minOrderAmount || 0,
            ts: Date.now(),
          };
        } else {
          record = { none: true, ts: Date.now() };
        }
        try { sessionStorage.setItem(DZ_IP_KEY, JSON.stringify(record)); } catch (e) {}
        try { window.dispatchEvent(new CustomEvent('dz:zone')); } catch (e) {}
        return record;
      })
      .catch(function () {
        window.__dzIpZonePromise = null;
        return null;
      });
    return window.__dzIpZonePromise;
  }

  function dzFmt(nis) {
    return '₪' + Math.max(0, Math.ceil(nis)).toLocaleString('he-IL');
  }

  class UpsellShipbar extends HTMLElement {
    connectedCallback() {
      this._onZone = this.apply.bind(this);
      window.addEventListener('dz:zone', this._onZone);
      window.addEventListener('storage', this._onZone);
      this.apply();
    }

    disconnectedCallback() {
      window.removeEventListener('dz:zone', this._onZone);
      window.removeEventListener('storage', this._onZone);
    }

    /* Write text/width/class only when the value actually changed, so the
       cart page's MutationObservers stay quiet after the first paint. */
    paint(text, pct, done, visible) {
      if (visible === false) {
        if (this.style.display !== 'none') this.style.display = 'none';
        return;
      }
      if (this.style.display === 'none') this.style.display = '';
      var textEl = this.querySelector('.upsell-shipbar__text');
      var fillEl = this.querySelector('.upsell-shipbar__fill');
      if (!textEl || !fillEl) return;
      if (text !== null && textEl.textContent.trim() !== text.trim()) {
        textEl.textContent = text;
      }
      if (pct !== null) {
        var w = pct + '%';
        if (fillEl.style.width !== w) fillEl.style.width = w;
      }
      if (done !== null && this.classList.contains('upsell-shipbar--done') !== done) {
        this.classList.toggle('upsell-shipbar--done', done);
      }
    }

    renderZone(zone) {
      if (zone.allowed !== true || !(zone.freeDeliveryAbove > 0)) {
        this.paint(null, null, null, false);
        return;
      }
      var total = Number(this.dataset.cartTotal || 0) / 100; // NIS
      var zoneName = (zone.zoneName || '').trim() || 'האזור שלך';
      var minOrder = Number(zone.minOrderAmount || 0);
      var threshold = Number(zone.freeDeliveryAbove);
      var pct = Math.min(100, Math.round((total * 100) / threshold));
      var text, done;

      if (minOrder > 0 && total < minOrder) {
        text = (this.dataset.zoneMinorder || '')
          .replace('{min}', dzFmt(minOrder))
          .replace('{amount}', dzFmt(minOrder - total));
        done = false;
      } else if (total >= threshold) {
        text = (this.dataset.zoneDone || '').replace('{zone}', zoneName);
        done = true;
      } else {
        text = (this.dataset.zoneProgress || '')
          .replace('{zone}', zoneName)
          .replace('{amount}', dzFmt(threshold - total));
        done = false;
      }
      this.paint(text, pct, done, true);
    }

    apply() {
      var zone = dzZoneInfo();
      if (zone) {
        this.renderZone(zone);
        return;
      }
      var ip = dzIpZone();
      if (ip) {
        if (ip.none) { this.applyUnknown(); } else { this.renderZone(ip); }
        return;
      }
      var self = this;
      dzFetchIpZone().then(function (record) {
        // a real address check may have landed meanwhile — it wins
        var fresh = dzZoneInfo();
        if (fresh) { self.renderZone(fresh); return; }
        if (record && !record.none) { self.renderZone(record); } else { self.applyUnknown(); }
      });
    }

    /* Zone unknown: below the universal minimum-order the server-rendered
       min-stage is accurate everywhere; above it we hide the bar instead of
       promising a threshold that may be wrong for the visitor's city. */
    applyUnknown() {
      var total = Number(this.dataset.cartTotal || 0) / 100;
      var minOrder = Number(this.dataset.defaultMinorder || 0);
      if (minOrder > 0 && total < minOrder) {
        this.paint(null, null, false, true); // keep server-rendered min-stage text
      } else {
        this.paint(null, null, null, false);
      }
    }
  }

  customElements.define('upsell-shipbar', UpsellShipbar);

  function upsellFetchConfig() {
    if (typeof fetchConfig === 'function') {
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      return config;
    }
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
    };
  }

  class UpsellWidget extends HTMLElement {
    connectedCallback() {
      /* mark upgraded so the "show more" control appears only when JS is
         live (without it we simply show the first 3 and no dead button). */
      this.classList.add('upsell--ready');
      this.addEventListener('click', (event) => {
        const more = event.target.closest('[data-upsell-more]');
        if (more) {
          event.preventDefault();
          this.classList.add('upsell--expanded');
          return;
        }
        const bundle = event.target.closest('[data-upsell-bundle]');
        if (bundle) {
          event.preventDefault();
          this.addBundle(bundle);
          return;
        }
        const zoom = event.target.closest('.upsell-card__media[data-upsell-zoom]');
        if (zoom) {
          event.preventDefault();
          const zi = zoom.querySelector('img');
          this.openZoom(zoom.getAttribute('data-upsell-zoom'), zi ? zi.alt : '', zoom);
          return;
        }
        const button = event.target.closest('[data-upsell-add]');
        if (button) {
          if (button.classList.contains('is-loading')) return;
          event.preventDefault();
          this.addToCart(button);
          return;
        }
        /* Click anywhere else on a card → open its product page (the image
           zooms and the button adds — both handled above; the cart drawer is
           excluded so a click there doesn't leave checkout). */
        const card = event.target.closest('.upsell-card[data-upsell-href]');
        if (card && this.getAttribute('data-context') !== 'cart' && !event.target.closest('a, button')) {
          window.location.href = card.getAttribute('data-upsell-href');
        }
      });
    }

    /* Tap a small offer thumbnail to see it big — a lightweight lightbox (the
       thumbnails are tiny in the horizontal product-page rows). */
    openZoom(url, alt, trigger) {
      if (!url) return;
      const existing = document.querySelector('.upsell-zoom');
      if (existing && typeof existing._upsellClose === 'function') existing._upsellClose(false);
      else if (existing) existing.remove();
      const overlay = document.createElement('div');
      overlay.className = 'upsell-zoom';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-label', (trigger && trigger.dataset.upsellDialogLabel) || 'תצוגת תמונה מוגדלת');
      const img = document.createElement('img');
      img.src = url;
      img.alt = alt || '';
      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'upsell-zoom__close';
      closeBtn.setAttribute('aria-label', (trigger && trigger.dataset.upsellCloseLabel) || 'סגירת תמונה');
      closeBtn.textContent = '×';
      overlay.appendChild(closeBtn);
      overlay.appendChild(img);
      const previousOverflow = document.body.style.overflow;
      const close = (restoreFocus = true) => {
        if (!overlay.isConnected) return;
        overlay.remove();
        document.removeEventListener('keydown', onKey);
        document.body.style.overflow = previousOverflow;
        if (restoreFocus && trigger && typeof trigger.focus === 'function') trigger.focus();
      };
      const onKey = (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          close();
          return;
        }
        if (e.key === 'Tab') {
          e.preventDefault();
          closeBtn.focus();
        }
      };
      overlay._upsellClose = close;
      closeBtn.addEventListener('click', () => close());
      overlay.addEventListener('click', (event) => {
        if (event.target === overlay) close();
      });
      document.addEventListener('keydown', onKey);
      document.body.appendChild(overlay);
      document.body.style.overflow = 'hidden';
      closeBtn.focus();
    }

    /* Bundle "add all": add every variant in one /cart/add.js call, then apply
       the merchant's discount code via /discount (lands on the cart with the
       whole package priced at X). No code → just open the cart. */
    addBundle(btn) {
      if (btn.classList.contains('is-loading')) return;
      const ids = (btn.dataset.upsellBundle || '').split(',').map((s) => s.trim()).filter(Boolean);
      if (!ids.length) return;
      const code = (btn.dataset.bundleCode || '').trim();
      btn.classList.add('is-loading');

      const items = ids.map((id) => ({ id: Number(id), quantity: 1 }));
      const config = upsellFetchConfig();
      config.body = JSON.stringify({ items: items });
      const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

      fetch(addUrl, config)
        .then((response) => response.json())
        .then((data) => {
          if (data.status) throw new Error(data.description || data.message || 'bundle add failed');
          try {
            if (window.dataLayer) {
              window.dataLayer.push({ event: 'upsell_add_bundle', item_count: items.length, discount_code: code });
            }
          } catch (e) {
            /* analytics only */
          }
          const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
          if (code) {
            window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(cartUrl);
          } else {
            window.location.href = cartUrl;
          }
        })
        .catch(() => {
          btn.classList.remove('is-loading');
        });
    }

    addToCart(button) {
      button.classList.add('is-loading');

      const drawer = document.querySelector('cart-drawer');
      const cartPageItems = document.querySelector('cart-items');
      const onCartPage = !!document.getElementById('main-cart-items') && !!cartPageItems;

      let sections = [];
      let pageTargets = null;
      if (onCartPage && typeof cartPageItems.getSectionsToRender === 'function') {
        pageTargets = cartPageItems.getSectionsToRender();
        sections = pageTargets.map((s) => s.section);
      } else if (drawer && typeof drawer.getSectionsToRender === 'function') {
        sections = drawer.getSectionsToRender().map((s) => s.id);
      }

      const config = upsellFetchConfig();
      config.body = JSON.stringify({
        items: [{ id: Number(button.dataset.upsellAdd), quantity: 1 }],
        sections: sections,
        sections_url: window.location.pathname,
      });

      const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

      fetch(addUrl, config)
        .then((response) => response.json())
        .then((data) => {
          if (data.status) {
            throw new Error(data.description || data.message || 'add failed');
          }

          try {
            if (window.dataLayer) {
              window.dataLayer.push({
                event: 'upsell_add_to_cart',
                upsell_rule: button.dataset.upsellRule || '',
                item_variant_id: button.dataset.upsellAdd,
                item_name: button.dataset.productTitle || '',
              });
            }
          } catch (e) {
            /* analytics only */
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'upsell-widget',
              productVariantId: button.dataset.upsellAdd,
              cartData: data,
            });
          }

          button.classList.remove('is-loading');
          button.classList.add('is-added');

          const feedback = new CustomEvent('deco:upsell-added', {
            bubbles: true,
            cancelable: true,
            detail: { variantId: String(button.dataset.upsellAdd), button, cartData: data },
          });
          this.dispatchEvent(feedback);

          if (onCartPage && pageTargets) {
            this.renderCartPage(pageTargets, data);
          } else if (drawer && typeof drawer.renderContents === 'function') {
            drawer.renderContents(data, { open: !feedback.defaultPrevented });
          } else {
            window.location.href = (window.routes && window.routes.cart_url) || '/cart';
          }
        })
        .catch(() => {
          button.classList.remove('is-loading');
          button.classList.remove('is-added');
        });
    }

    renderCartPage(targets, data) {
      if (!data.sections) return;
      targets.forEach((target) => {
        const container = document.getElementById(target.id);
        const html = data.sections[target.section];
        if (!container || !html) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const source = parsed.querySelector(target.selector);
        const destination = container.querySelector(target.selector) || container;
        if (source) destination.innerHTML = source.innerHTML;
      });

      const cartItemsEl = document.querySelector('cart-items');
      const cartFooter = document.getElementById('main-cart-footer');
      if (cartItemsEl) cartItemsEl.classList.remove('is-empty');
      if (cartFooter) cartFooter.classList.remove('is-empty');
    }
  }

  customElements.define('upsell-widget', UpsellWidget);
})();
